/**
 * ContentCard 스타일
 * @description 컴포넌트 토큰이 없어 시맨틱으로 조립한다. 앱 `ContentCard.styles.ts` 와 같은 값.
 *
 *   Media 는 radius 를 갖지 않는 컴포넌트라 둥근 모서리는 여기서 클리핑 상자로 입힌다.
 *   배경이 없고 세로 패딩도 없다 — 담는 쪽이 정한다. 텍스트 블록만 가로로 4 를 들여쓴다.
 *
 *   ## 값은 전부 시안 토큰이다
 *   제목 `l2Tight`(14/16) · 곁글 `caption`(12/16) · 사이 `gap.xs`(4). 자리를 못 박을 때의
 *   두 줄도 그 행간으로 센다(16×2=32) — `texts` 의 `min-height` 가 제목 두 줄 + 간격 + 곁글
 *   한 줄(52)을 쥔다. 제목은 제 글자만큼만 차지하고 남는 자리는 블록 맨 아래에 모인다.
 *   그래야 제목과 곁글 사이가 어떤 글에서도 정확히 4 다.
 *
 *   제목의 위아래 여백 한 쌍은 **첫 줄 이모지가 그려질 자리**다(앱과 같은 처리). 서로
 *   상쇄되므로 글자 위치도 블록 높이도 바뀌지 않는다.
 */
import { cva } from "class-variance-authority";

export const contentCardStyles = {
  root: cva("flex w-full flex-col gap-[var(--fw-space-gap-sm)] text-left", {
    variants: {
      interactive: {
        true: "group cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fw-color-border-focused)] [&_p:first-of-type]:transition-colors group-hover:[&_p:first-of-type]:text-[var(--fw-color-content-neutral-primary)]",
        false: "",
      },
    },
    defaultVariants: { interactive: false },
  }),
  /**
   * Media 에 모서리를 입히는 클리핑 상자. 누를 수 있는 카드는 마우스가 올라오면 그림이
   * 천천히 커진다 — 상자가 잘라 주므로 카드 크기는 그대로다. 움직임을 줄인 설정에서는 없다.
   */
  mediaClip:
    "overflow-hidden rounded-[var(--fw-radius-lg)] [&_img]:transition-transform [&_img]:duration-700 [&_img]:ease-out motion-safe:group-hover:[&_img]:scale-[1.05]",
  texts: cva(
    "flex flex-col gap-[var(--fw-space-gap-xs)] px-[var(--fw-space-inset-xs)]",
    {
      variants: {
        fixed: {
          /* 제목 두 줄 32 + 간격 4 + 곁글 16 = 52. 남는 자리는 맨 아래로 */
          true: "min-h-[calc(var(--fw-font-l2-tight-line-height)*2+var(--fw-space-gap-xs)+var(--fw-font-caption-line-height))]",
          false: "",
        },
      },
      defaultVariants: { fixed: false },
    },
  ),
  title: cva(
    "fw-l2-tight -mt-[var(--fw-space-gap-sm)] pt-[var(--fw-space-gap-sm)] text-[var(--fw-color-content-neutral-secondary)]",
    {
      variants: { lines: { 1: "line-clamp-1", 2: "line-clamp-2" } },
      defaultVariants: { lines: 2 },
    },
  ),
  metadata: cva("fw-caption text-[var(--fw-color-content-neutral-tertiary)]", {
    variants: { lines: { 1: "line-clamp-1", 2: "line-clamp-2" } },
    defaultVariants: { lines: 1 },
  }),
};
